/**
 * Operations orders, missions 21-26: intelligence (10 §2). Each states its special rule as an order, names
 * the equipment the job needs, and says how it ends. tests/unit/uiBriefing.test.ts holds a phrase from every
 * rule here and must be kept in step.
 */

import type { ArchetypeOrder } from './orders';
import { ALERTED, fixed, NO_NOISE_RULE, NOTHING_SPECIAL, ON_THE_CLOCK, QUIET_PISTOL, rules } from './orderKit';

type IntelligenceId =
  | 'steal-plans' | 'wiretap' | 'photograph-convoy' | 'black-box' | 'forward-observer' | 'courier-ambush';

export const INTELLIGENCE_ORDERS: Readonly<Record<IntelligenceId, ArchetypeOrder>> = {
  'steal-plans': {
    situation: 'A HEADQUARTERS BUILDING INSIDE A FENCED COMPOUND. THE PLANS ARE IN ITS SAFE, AND THE STAFF HAVE'
      + ' STANDING ORDERS TO BURN THEM.',
    mission: fixed('TAKE THE DOCUMENTS FROM THE SAFE AND BRING THEM OUT.'),
    execution: [
      'WALK INTO THE HQ BUILDING. AT THE SAFE, HOLD INTERACT FOR SIX SECONDS, STANDING STILL. MOVE OR TAKE A HIT'
        + ' AND YOU START AGAIN.',
      'DO NOT BLOW THE SAFE: WHAT IS IN IT BURNS WITH IT, AND THE MISSION HAS FAILED.',
    ],
    special: rules(
      `KNIFE ONLY UNTIL YOU ARE INSIDE THE HQ FENCE. ${ALERTED} BEFORE YOU ARE INSIDE THE FENCE, THE STAFF BURN THE`
        + ' DOCUMENTS 40 SECONDS LATER AND THE MISSION HAS FAILED.',
      'ONCE YOU ARE INSIDE THE FENCE THEY HAVE NO TIME TO BURN ANYTHING.',
    ),
    equipment: [NOTHING_SPECIAL, QUIET_PISTOL],
  },
  wiretap: {
    situation: 'A FIELD TELEPHONE EXCHANGE SERVING THE ENEMY HEADQUARTERS. LIGHTLY GUARDED: NOBODY EXPECTS A VISITOR.',
    mission: fixed('FIT A TAP TO THE EXCHANGE AND LEAVE WITHOUT ANYONE KNOWING YOU CAME.'),
    execution: [
      'AT THE EXCHANGE, HOLD INTERACT FOR TEN SECONDS, STANDING STILL. MOVE OR TAKE A HIT AND YOU START AGAIN.',
      'DO NOT DESTROY THE EXCHANGE: A DEAD LINE CANNOT BE TAPPED, AND THE MISSION HAS FAILED.',
    ],
    special: rules(
      'IF YOU ARE SEEN WITHIN 25 M OF THE EXCHANGE - BEFORE OR AFTER THE TAP IS FITTED - THE TAP IS FOUND AND THE'
        + ' MISSION HAS FAILED.',
      'ONLY THOSE 25 M MATTER. ANYWHERE ELSE, BEING SEEN IS JUST A FIGHT.',
    ),
    equipment: [NOTHING_SPECIAL],
    time: 'ALLOW FOR TEN SECONDS AT THE EXCHANGE, UNSEEN.',
  },
  'photograph-convoy': {
    situation: 'A ROAD, AND AN OVERLOOK ABOVE IT. AN ENEMY CONVOY PASSES AT A STATED TIME.',
    mission: fixed('BE IN THE OVERLOOK, UNSEEN, WHILE THE CONVOY PASSES, AND PHOTOGRAPH IT.'),
    execution: [
      'THE OVERLOOK IS MARKED. BE INSIDE IT BEFORE THE CONVOY COMES AND STAY INSIDE IT WHILE IT PASSES.',
      'THE CAMERA ONLY WORKS WHILE THE CONVOY IS ON THE ROAD. TIME SPENT IN THE OVERLOOK BEFORE THAT COUNTS FOR'
        + ' NOTHING.',
    ],
    special: rules(
      `THE CONVOY PASSES FOR 40 SECONDS, AT A STATED TIME. ${ON_THE_CLOCK} ARRIVE LATE AND THE MISSION IS LOST:`
        + ' THERE IS NO SECOND CONVOY.',
      'IF YOU ARE SEEN AT THE OVERLOOK THE MISSION HAS FAILED. ONLY THE OVERLOOK MATTERS: ANYWHERE ELSE, BEING'
        + ' SEEN IS JUST A FIGHT.',
    ),
    equipment: [`${NOTHING_SPECIAL} TRAVEL LIGHT: YOU ARE RACING A TIMETABLE.`],
    time: 'THE CONVOY KEEPS ITS OWN TIME, NOT YOURS. BE IN THE OVERLOOK EARLY.',
  },
  'black-box': {
    situation: 'ONE OF OUR AIRCRAFT IS DOWN. ITS POSITION IS ONLY ROUGHLY KNOWN: THREE POSSIBLE CRASH SITES ARE'
      + ' MARKED, AND ONE OF THEM IS REAL. THE ENEMY IS LOOKING FOR IT TOO.',
    mission: fixed('FIND THE WRECK, TAKE THE FLIGHT RECORDER AND BRING IT OUT.'),
    execution: [
      'CHECK THE SITES. ONLY THE REAL WRECK HAS A RECORDER TO TAKE: HOLD INTERACT AT IT, STANDING STILL.',
      'LOOK FOR THE WRECK ON THE PASS OVER THE TARGET AND SAVE YOURSELF TWO WALKS.',
    ],
    special: rules(
      `AN ENEMY SEARCH PARTY CONVERGES ON THE REAL SITE AT A STATED TIME. ${ON_THE_CLOCK} BE THERE FIRST, OR FIGHT`
        + ' THEM FOR IT.',
      NO_NOISE_RULE,
    ),
    equipment: [NOTHING_SPECIAL],
  },
  'forward-observer': {
    situation: 'ENEMY POSITIONS OVERLOOKED BY THREE OBSERVATION POINTS. AN AIR STRIKE IS WAITING ON YOUR MARKS.',
    mission: fixed('REACH ALL THREE OBSERVATION POINTS AND HOLD EACH FOR 8 SECONDS TO MARK ITS TARGET.'),
    execution: [
      'STAND IN EACH POINT FOR 8 SECONDS. STEP OUT EARLY AND THAT POINT STARTS AGAIN.',
      'PLAN THE ORDER: MARK THE POINT NEAREST YOUR WAY OUT LAST.',
    ],
    special: rules(
      'IF YOU ARE SEEN WHILE YOU ARE AT A POINT, THAT MARK IS CANCELLED AND ITS 8 SECONDS START AGAIN.',
      'ON THE THIRD MARK THE AIR STRIKE COMES IN. BE 60 M CLEAR OF EVERYTHING YOU MARKED WHEN IT LANDS, OR IT'
        + ' LANDS ON YOU. YOUR HUD SHOWS THE COUNT AND THE DISTANCE.',
    ),
    equipment: [NOTHING_SPECIAL],
  },
  'courier-ambush': {
    situation: 'A COURIER CARRIES DISPATCHES ALONG THE ROAD ON FOOT, WITH AN ESCORT, AT A STATED TIME.',
    mission: fixed('KILL THE COURIER AND TAKE THE SATCHEL FROM HIS BODY.'),
    execution: [
      `HE COMES AT A STATED TIME. ${ON_THE_CLOCK} BE IN COVER BY THE ROAD BEFORE IT RUNS OUT.`,
      'HE MUST BE DOWN BEFORE YOU CAN TAKE THE SATCHEL: INTERACT AT THE BODY. KILLING HIM IS NOT ENOUGH. THE'
        + ' SATCHEL IS THE MISSION.',
    ],
    special: rules(
      `KNIFE ONLY UNTIL THE COURIER IS ON THE ROAD. ${ALERTED} BEFORE THE AMBUSH, HE TURNS BACK AND THE MISSION HAS`
        + ' FAILED. HE DOES NOT TRY AGAIN.',
      'ONCE HE IS IN FRONT OF YOU, SHOOT. THE ESCORT WILL.',
    ),
    equipment: [NOTHING_SPECIAL, QUIET_PISTOL],
  },
};
